import { useState, useCallback } from 'react';
import { fetchGraphql } from '../graphql/fetch';

export type RegistrationStatus = 'idle' | 'submitting' | 'success' | 'error';

export interface RegisterArgs {
  accepted: boolean;
  additionalAccepted?: boolean;
  registrationFields?: Record<string, string>;
}

export interface RegistrationResult {
  registered: boolean;
  redirectUrl: string | null;
}

export class RegistrationError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = 'RegistrationError';
  }
}

const MUTATION = `
  mutation RegisterForChallenge($slug: ID!, $accepted: Boolean!, $additional: Boolean, $fields: JSON) {
    registerForChallenge(input: { challengeSlug: $slug, accepted: $accepted, additionalAccepted: $additional, registrationFields: $fields }) {
      registered redirectUrl errors { code message }
    }
  }
`;

export function useRegistration({ challengeSlug }: { challengeSlug: string }) {
  const [status, setStatus]       = useState<RegistrationStatus>('idle');
  const [error, setError]         = useState<RegistrationError | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  const register = useCallback(async (args: RegisterArgs): Promise<RegistrationResult> => {
    setStatus('submitting');
    setError(null);

    try {
      const res = await fetchGraphql<{
        registerForChallenge: {
          registered: boolean;
          redirectUrl: string | null;
          errors: Array<{ code: string; message: string }>;
        };
      }>({
        query: MUTATION,
        variables: {
          slug:       challengeSlug,
          accepted:   args.accepted,
          additional: args.additionalAccepted ?? false,
          fields:     args.registrationFields ?? null,
        },
      });

      if (res.errors && res.errors.length) {
        throw new RegistrationError('GRAPHQL', res.errors[0].message);
      }

      const payload = res.data && res.data.registerForChallenge;
      if (!payload) {
        throw new RegistrationError('UNKNOWN', 'registerForChallenge returned no payload');
      }
      if (!payload.registered) {
        const e = payload.errors && payload.errors[0];
        throw new RegistrationError(e ? e.code || 'UNKNOWN' : 'UNKNOWN', e ? e.message || 'Registration failed' : 'Registration failed');
      }

      setRedirectUrl(payload.redirectUrl ?? null);
      setStatus('success');
      return { registered: true, redirectUrl: payload.redirectUrl ?? null };
    } catch (err) {
      const tErr = err instanceof RegistrationError
        ? err
        : new RegistrationError('UNKNOWN', String(err));
      setError(tErr);
      setStatus('error');
      throw tErr;
    }
  }, [challengeSlug]);

  return { register, status, error, redirectUrl };
}
